<script setup lang="ts">
import InputWrapper, { InputWrapperProps } from '@/atoms/InputWrapper.vue';
import BaseInput, { BaseInputProps, InputState } from '@/atoms/BaseInput.vue';
import LocalIcon from '@/atoms/LocalIcon.vue';
import BaseDropdown from '@/molecules/BaseDropdown.vue';
import { computed, onMounted, ref, watch } from 'vue';
import {
  allowOnlyNumbers,
  extractCountryFromPhone,
  formatToInternationalPhoneFormat,
} from '@/helpers/input.helpers';
import { ENTER_KEY_HINTS } from '@/constant/inputs';
import { PHONE_COUNTRIES, DEFAULT_COUNTRY, type CountrySelectItem } from '@/constant/phone';
export type { CountrySelectItem } from '@/constant/phone';
export type PhoneInputProps = InputWrapperProps &
  BaseInputProps & {
    iconName?: string;
  };

const defaultCountry = DEFAULT_COUNTRY;
const countries = PHONE_COUNTRIES;

const selectedCountry = ref<CountrySelectItem>(defaultCountry);

const selectedCountryKey = computed({
  get() {
    return selectedCountry.value.key;
  },
  set(key: string) {
    const country = countries.find((c) => c.key === key);
    if (country) selectedCountry.value = country;
  },
});
const props = withDefaults(defineProps<PhoneInputProps>(), {
  enterkeyhint: ENTER_KEY_HINTS.done,
});
const state = ref<InputState>(props.state ?? 'default');
const phoneNumber = defineModel<string>();
// Chiffres tels que tapés : le modèle peut différer (0 national retiré), on garde
// l'affichage de la saisie pour ne pas faire sauter le curseur.
const typedDigits = ref<string>('');

const onEnter = (e: KeyboardEvent) => {
  if (props.enterkeyhint === ENTER_KEY_HINTS.done) {
    (e.target as HTMLInputElement)?.blur();
  }
};

onMounted(() => {
  if (phoneNumber.value) {
    state.value = 'filled';
    const detected = extractCountryFromPhone(phoneNumber.value, countries);
    if (detected) selectedCountry.value = detected;
  }
});

watch(phoneNumber, (val) => {
  if (state.value === 'focused' || state.value === 'disabled') return;
  state.value = val ? 'filled' : 'default';
});

watch(selectedCountry, (newCountry, oldCountry) => {
  if (!phoneNumber.value) return;
  const digits = phoneNumber.value.startsWith(oldCountry.prefix)
    ? phoneNumber.value.slice(oldCountry.prefix.length)
    : phoneNumber.value.replace(/^\+\d+/, '');
  if (digits) phoneNumber.value = newCountry.prefix + digits;
});

const setFocused = () => {
  if (state.value !== 'disabled') {
    state.value = 'focused';
  }
};

const setDefault = () => {
  if (state.value !== 'disabled') {
    state.value = phoneNumber.value ? 'filled' : 'default';
  }
};

const formattedPhoneNumber = computed({
  get() {
    if (!phoneNumber.value) return '';
    if (
      typedDigits.value &&
      formatToInternationalPhoneFormat(typedDigits.value, selectedCountry.value.prefix) ===
        phoneNumber.value
    ) {
      return typedDigits.value;
    }
    if (phoneNumber.value.startsWith(selectedCountry.value.prefix)) {
      return phoneNumber.value.slice(selectedCountry.value.prefix.length);
    }
    const detected = extractCountryFromPhone(phoneNumber.value, countries);
    if (detected) return phoneNumber.value.slice(detected.prefix.length);
    return phoneNumber.value;
  },
  set(input: string) {
    typedDigits.value = input.replace(/\D/g, '');
    phoneNumber.value = formatToInternationalPhoneFormat(
      typedDigits.value,
      selectedCountry.value.prefix,
    );
  },
});
</script>

<template>
  <InputWrapper
    :class="props.class"
    :hint="hint"
    :state="state"
    :destructive="destructive"
    :error="error"
    :label="label"
    :id="id"
  >
    <BaseInput
      :id="id"
      :destructive="destructive"
      :placeholder="placeholder"
      :field-props="fieldProps"
      :state="state"
      type="tel"
    >
      <BaseDropdown
        :id="id"
        type="text"
        v-model="selectedCountryKey"
        :items="countries"
        class="max-w-min m-0 py-0 pl-0 h-min"
      />
      <span>{{ selectedCountry.prefix }}</span>
      <input
        :data-cy="id"
        :id="id"
        v-bind="fieldProps"
        type="tel"
        class="w-full font-inter"
        v-model="formattedPhoneNumber"
        @input.capture="allowOnlyNumbers"
        @blur="setDefault"
        @click="setFocused"
        @focusin="setFocused"
        @keydown.enter="onEnter"
        :enterkeyhint="enterkeyhint"
        :autocomplete="autocomplete"
      />
      <LocalIcon v-if="iconName" size="small" :name="iconName" color="tertiary" />
    </BaseInput>
  </InputWrapper>
</template>
